import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Input } from 'src/@/components/ui/input';
import { Checkbox } from 'src/@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/@/components/ui/dialog';
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import ModalCustomField from './ModalCustomField';
import { FUNIL_DESIGN as D } from './FunilComponent.design';

import v2_actionListarCustomTabs from 'src/actions/v2/crm/v2_actionListarCustomTabs';
import v2_actionCriarCustomTab from 'src/actions/v2/crm/v2_actionCriarCustomTab';
import v2_actionEditarCustomTab from 'src/actions/v2/crm/v2_actionEditarCustomTab';
import v2_actionDeletarCustomTab from 'src/actions/v2/crm/v2_actionDeletarCustomTab';
import v2_actionCriarCustomField from 'src/actions/v2/crm/v2_actionCriarCustomField';
import v2_actionEditarCustomField from 'src/actions/v2/crm/v2_actionEditarCustomField';
import v2_actionDeletarCustomField from 'src/actions/v2/crm/v2_actionDeletarCustomField';

const TIPO_COLORS = {
  texto: 'text-[#71717a] border-[#e7e7ec] bg-[#f4f4f5]',
  selecao: 'text-[#5b21b6] border-[#ddd6fe] bg-[#ede9fe]',
  data: 'text-[#b54708] border-[#fedf89] bg-[#fffaeb]',
  upload: 'text-[#5b21b6] border-[#ddd6fe] bg-[#ede9fe]',
};

export default function TabCustomFields({ cliente, pipelines }) {
  const { t } = useTranslation('settings');
  const TIPO_LABELS = {
    texto: t('funnel.customFields.types.text', 'Texto'),
    selecao: t('funnel.customFields.types.selection', 'Seleção'),
    data: t('funnel.customFields.types.date', 'Data'),
    upload: t('funnel.customFields.types.upload', 'Upload'),
  };
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [tabs, setTabs] = useState([]);
  const [expandedTabs, setExpandedTabs] = useState({});

  const [openTabModal, setOpenTabModal] = useState(false);
  const [actionTab, setActionTab] = useState(null);
  const [tabModalId, setTabModalId] = useState(null);
  const [tabModalNome, setTabModalNome] = useState('');
  const [tabModalPipelineIds, setTabModalPipelineIds] = useState([]);

  const [openFieldModal, setOpenFieldModal] = useState(false);
  const [actionField, setActionField] = useState(null);
  const [currentTabId, setCurrentTabId] = useState(null);
  const [fieldId, setFieldId] = useState(null);
  const [fieldNome, setFieldNome] = useState('');
  const [fieldTipo, setFieldTipo] = useState('texto');
  const [fieldOpcoes, setFieldOpcoes] = useState([]);
  const [fieldObrigatorio, setFieldObrigatorio] = useState(false);

  const [openConfirmDeleteTab, setOpenConfirmDeleteTab] = useState(false);
  const [deleteTabId, setDeleteTabId] = useState(null);
  const [openConfirmDeleteField, setOpenConfirmDeleteField] = useState(false);
  const [deleteFieldId, setDeleteFieldId] = useState(null);

  useEffect(() => {
    if (selectedPipeline) {
      buscarTabs();
    }
  }, [selectedPipeline]);

  async function buscarTabs() {
    try {
      const result = await v2_actionListarCustomTabs(cliente._id, selectedPipeline);
      if (result.status === 200) {
        setTabs(result.data?.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  function getPipelineNome(id) {
    const p = pipelines?.find((p) => p._id === id || p._id === String(id));
    return p?.pipeline_nome || p?.nome || id;
  }

  function togglePipelineSelection(pipelineId) {
    setTabModalPipelineIds((prev) =>
      prev.includes(pipelineId)
        ? prev.filter((id) => id !== pipelineId)
        : [...prev, pipelineId]
    );
  }

  function openCriarTab() {
    setActionTab('Criar');
    setTabModalId(null);
    setTabModalNome('');
    setTabModalPipelineIds(selectedPipeline ? [selectedPipeline] : []);
    setOpenTabModal(true);
  }

  function openEditarTab(tab) {
    setActionTab('Editar');
    setTabModalId(tab._id);
    setTabModalNome(tab.nome);
    setTabModalPipelineIds((tab.pipeline_ids || []).map((id) => String(id)));
    setOpenTabModal(true);
  }

  async function handleSubmitTab() {
    if (!tabModalNome.trim() || tabModalPipelineIds.length === 0) return;
    try {
      if (actionTab === 'Criar') {
        await v2_actionCriarCustomTab(cliente._id, {
          nome: tabModalNome.trim(),
          pipeline_ids: tabModalPipelineIds,
        });
      } else {
        await v2_actionEditarCustomTab(tabModalId, {
          scope: {
            nome: tabModalNome.trim(),
            pipeline_ids: tabModalPipelineIds,
          },
        });
      }
      setOpenTabModal(false);
      buscarTabs();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleDeletarTab() {
    try {
      await v2_actionDeletarCustomTab(deleteTabId);
      setOpenConfirmDeleteTab(false);
      setDeleteTabId(null);
      buscarTabs();
    } catch (error) {
      setOpenConfirmDeleteTab(false);
      console.log(error);
    }
  }

  function resetFieldForm() {
    setActionField(null);
    setCurrentTabId(null);
    setFieldId(null);
    setFieldNome('');
    setFieldTipo('texto');
    setFieldOpcoes([]);
    setFieldObrigatorio(false);
  }

  async function handleCreateField() {
    try {
      await v2_actionCriarCustomField(cliente._id, {
        nome: fieldNome,
        tipo: fieldTipo,
        opcoes: fieldTipo === 'selecao' ? fieldOpcoes : [],
        obrigatorio: fieldObrigatorio,
        tab_id: currentTabId,
      });
      setOpenFieldModal(false);
      resetFieldForm();
      buscarTabs();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleEditField() {
    try {
      await v2_actionEditarCustomField(fieldId, {
        scope: {
          nome: fieldNome,
          tipo: fieldTipo,
          opcoes: fieldTipo === 'selecao' ? fieldOpcoes : [],
          obrigatorio: fieldObrigatorio,
        },
      });
      setOpenFieldModal(false);
      resetFieldForm();
      buscarTabs();
    } catch (error) {
      console.log(error);
    }
  }

  async function handleDeleteField() {
    try {
      await v2_actionDeletarCustomField(deleteFieldId);
      setOpenConfirmDeleteField(false);
      setDeleteFieldId(null);
      buscarTabs();
    } catch (error) {
      setOpenConfirmDeleteField(false);
      console.log(error);
    }
  }

  function toggleTab(tabId) {
    setExpandedTabs((prev) => ({ ...prev, [tabId]: !prev[tabId] }));
  }

  return (
    <>
      <ModalConfimacao
        open={openConfirmDeleteTab}
        onClose={() => setOpenConfirmDeleteTab(false)}
        onConfirm={handleDeletarTab}
        mensagem={t('funnel.customFields.deleteTabConfirm', 'Deseja mesmo excluir esta aba? Todos os campos serão desativados.')}
      />

      <ModalConfimacao
        open={openConfirmDeleteField}
        onClose={() => setOpenConfirmDeleteField(false)}
        onConfirm={handleDeleteField}
        mensagem={t('funnel.customFields.deleteFieldConfirm', 'Deseja mesmo excluir este campo?')}
      />

      <ModalCustomField
        open={openFieldModal}
        action={actionField}
        nome={fieldNome}
        changeNome={(e) => setFieldNome(e.target.value)}
        tipo={fieldTipo}
        changeTipo={(value) => setFieldTipo(value)}
        opcoes={fieldOpcoes}
        changeOpcoes={(value) => setFieldOpcoes(value)}
        obrigatorio={fieldObrigatorio}
        changeObrigatorio={(checked) => setFieldObrigatorio(checked)}
        onSubmit={actionField === 'Criar' ? handleCreateField : handleEditField}
        onClose={() => {
          setOpenFieldModal(false);
          resetFieldForm();
        }}
      />

      <Dialog open={openTabModal} onOpenChange={() => setOpenTabModal(false)}>
        <DialogContent className="sm:max-w-[425px]" style={{ fontFamily: D.fontStack }}>
          <DialogHeader>
            <DialogTitle className={D.dialogTitle}>
              {actionTab === 'Criar'
                ? t('funnel.customFields.tabDialog.createTitle', 'Criar aba personalizada')
                : t('funnel.customFields.tabDialog.editTitle', 'Editar aba personalizada')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-2">
            <div className={D.field}>
              <label className={D.label}>{t('funnel.customFields.tabDialog.nameLabel', 'Nome da aba')}</label>
              <Input
                value={tabModalNome}
                onChange={(e) => setTabModalNome(e.target.value)}
                placeholder={t('funnel.customFields.tabDialog.namePlaceholder', 'Ex: Renovação, Sinistros')}
                className={D.input}
              />
            </div>
            <div className={D.field}>
              <label className={D.label}>{t('funnel.customFields.tabDialog.pipelinesLabel', 'Pipelines vinculados')}</label>
              <p className={D.hint} style={{ marginTop: 0, marginBottom: 7 }}>
                {t('funnel.customFields.tabDialog.pipelinesHelper', 'Selecione em quais funis essa aba será exibida')}
              </p>
              <div className="space-y-0.5 max-h-[200px] overflow-auto border border-[#d4d4d8] rounded-lg p-1.5">
                {pipelines?.map((p) => (
                  <label
                    key={p._id}
                    className="flex items-center gap-2.5 px-[13px] py-[11px] rounded-lg hover:bg-[#f4f4f7] cursor-pointer"
                  >
                    <Checkbox
                      checked={tabModalPipelineIds.includes(p._id)}
                      onCheckedChange={() => togglePipelineSelection(p._id)}
                    />
                    <span className="text-[14px] text-[#18181b]">{p.pipeline_nome || p.nome}</span>
                  </label>
                ))}
              </div>
              {tabModalPipelineIds.length === 0 && (
                <p className="mt-[7px] text-[12px] font-medium text-[#b42318]">
                  {t('funnel.customFields.tabDialog.pipelinesRequired', 'Selecione pelo menos um pipeline')}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className={D.dialogFooter}>
            <button type="button" className={D.ghostButton} onClick={() => setOpenTabModal(false)}>
              {t('buttons.cancel', { ns: 'common' })}
            </button>
            <button
              type="button"
              className={D.primaryButton}
              onClick={handleSubmitTab}
              disabled={!tabModalNome.trim() || tabModalPipelineIds.length === 0}
            >
              {actionTab === 'Criar' ? t('buttons.create', 'Criar', { ns: 'common' }) : t('buttons.save', 'Salvar', { ns: 'common' })}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className={D.stack} style={{ fontFamily: D.fontStack }}>
        <div className={D.sectionHeader}>
          <div className={D.sectionHeaderMain}>
            <h2 className={D.sectionHeaderTitle}>
              {t('funnel.core.tabs.customFields', 'Campos Personalizados')}
            </h2>
            <p className={D.sectionHeaderSub}>
              {t('funnel.customFields.sectionHint', 'Crie abas e campos extras para o card de negócio, por funil.')}
            </p>
          </div>
          {selectedPipeline && (
            <button type="button" onClick={openCriarTab} className={D.primaryButton}>
              {t('funnel.customFields.newTabButton', 'Nova aba')}
            </button>
          )}
        </div>

        <div className={D.card}>
          <div className={D.field}>
            <label className={D.label}>{t('funnel.customFields.pipelineSelectLabel', 'Funil')}</label>
            <Select value={selectedPipeline} onValueChange={setSelectedPipeline}>
              <SelectTrigger className={`${D.select} max-w-md`}>
                <SelectValue placeholder={t('funnel.customFields.pipelineSelectPlaceholder', 'Selecione um pipeline para visualizar')} />
              </SelectTrigger>
              <SelectContent>
                {pipelines?.map((p) => (
                  <SelectItem key={p._id} value={p._id}>{p.pipeline_nome || p.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className={D.hint}>
              {t('funnel.customFields.pipelineSelectHint', 'Os campos personalizados são configurados por funil.')}
            </p>
          </div>
        </div>

        {selectedPipeline && (
          <div className="flex flex-col gap-3.5">
            {tabs.map((tab) => {
              const isOpen = expandedTabs[tab._id];
              return (
                <div key={tab._id} className={D.disclosure}>
                  <button type="button" onClick={() => toggleTab(tab._id)} className={D.disclosureButton}>
                    <span className={`${D.disclosureCaret} ${isOpen ? D.disclosureCaretOpen : ''}`}>▶</span>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-[#18181b]">{tab.nome}</span>
                        <span className={`${D.badge} ${D.badgeNeutral}`}>
                          {t('funnel.customFields.fieldsCount', '{{count}} campos', { count: tab.fields?.length || 0 })}
                        </span>
                        {(tab.pipeline_ids || []).map((pid) => (
                          <span key={pid} className={`${D.badge} ${D.badgeSoft}`}>
                            {getPipelineNome(pid)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className={D.iconButton} onClick={() => openEditarTab(tab)}>
                        <Iconify icon="eva:edit-2-outline" width={14} />
                      </button>
                      <button
                        type="button"
                        className={D.iconButtonDanger}
                        onClick={() => {
                          setDeleteTabId(tab._id);
                          setOpenConfirmDeleteTab(true);
                        }}
                      >
                        <Iconify icon="eva:trash-2-outline" width={14} />
                      </button>
                    </div>
                  </button>

                  {isOpen && (
                    <div className={D.disclosureBody}>
                      {tab.fields?.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-b border-[#f0f0f3] hover:bg-transparent">
                              <TableHead className={D.tableHead}>{t('funnel.customFields.table.fieldColumn', 'Campo')}</TableHead>
                              <TableHead className={D.tableHead}>{t('labels.type', 'Tipo', { ns: 'common' })}</TableHead>
                              <TableHead className={D.tableHead}>{t('funnel.customFields.table.requiredColumn', 'Obrigatório')}</TableHead>
                              <TableHead className="w-[100px]" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tab.fields.map((field, index) => (
                              <TableRow key={index} className={D.tableRow}>
                                <TableCell className={`${D.tableCell} font-medium`}>{field.nome}</TableCell>
                                <TableCell>
                                  <span className={`${D.badge} ${TIPO_COLORS[field.tipo]}`}>
                                    {TIPO_LABELS[field.tipo]}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {field.obrigatorio ? (
                                    <span className={`${D.badge} ${D.badgeOk}`}>
                                      <span className={`${D.badgeDot} ${D.badgeDotOk}`} />
                                      {t('buttons.yes', 'Sim', { ns: 'common' })}
                                    </span>
                                  ) : (
                                    <span className="text-[13px] text-[#a1a1aa]">{t('buttons.no', 'Não', { ns: 'common' })}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1.5 justify-end">
                                    <button
                                      type="button"
                                      className={D.iconButton}
                                      onClick={() => {
                                        setOpenFieldModal(true);
                                        setActionField('Editar');
                                        setCurrentTabId(tab._id);
                                        setFieldId(field._id);
                                        setFieldNome(field.nome);
                                        setFieldTipo(field.tipo);
                                        setFieldOpcoes(field.opcoes || []);
                                        setFieldObrigatorio(field.obrigatorio);
                                      }}
                                    >
                                      <Iconify icon="eva:edit-2-outline" width={14} />
                                    </button>
                                    <button
                                      type="button"
                                      className={D.iconButtonDanger}
                                      onClick={() => {
                                        setDeleteFieldId(field._id);
                                        setOpenConfirmDeleteField(true);
                                      }}
                                    >
                                      <Iconify icon="eva:trash-2-outline" width={14} />
                                    </button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className={D.emptyState}>
                          <p className={D.emptyDesc}>{t('funnel.customFields.table.empty', 'Nenhum campo configurado nesta aba')}</p>
                        </div>
                      )}
                      <div className="px-[18px] py-3.5 border-t border-[#f0f0f3]">
                        <button
                          type="button"
                          className={D.link}
                          onClick={() => {
                            setOpenFieldModal(true);
                            setActionField('Criar');
                            setCurrentTabId(tab._id);
                          }}
                        >
                          {t('funnel.customFields.addFieldButton', 'Adicionar campo')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {tabs.length === 0 && (
              <div className={`${D.card} ${D.emptyState}`}>
                <p className={D.emptyDesc}>
                  {t('funnel.customFields.emptyTabs', 'Nenhuma aba personalizada cadastrada para este pipeline')}
                </p>
              </div>
            )}
          </div>
        )}

        {!selectedPipeline && (
          <div className={D.noticeInfo}>
            {t('funnel.customFields.selectPipelinePrompt', 'Selecione um pipeline para configurar campos personalizados')}
          </div>
        )}
      </div>
    </>
  );
}
